import React, { useState, useEffect, useRef } from "react";
import ImageUploadField from "./ImageUploadField";
import { LetterHeadDetails } from "../PDFExportConfigModal";

interface LetterHeadManagementProps {
	letterHeads: LetterHeadDetails[];
	availableOffices: string[];
	organizationId: string;
	onLetterHeadsChange: (letterHeads: LetterHeadDetails[]) => void;
}

const LetterHeadManagement: React.FC<LetterHeadManagementProps> = ({
	letterHeads,
	availableOffices,
	organizationId,
	onLetterHeadsChange,
}) => {
	// Track temporary string values for number inputs to allow clearing
	const [tempValues, setTempValues] = useState<{ [key: string]: string }>({});
	// Track which letterheads are expanded (by index)
	const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set([0]));
	// Refs for scrollable office containers
	const officeContainerRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

	// Clean up invalid expanded indices when letterheads array changes
	useEffect(() => {
		setExpandedIndices((prev) => {
			// Remove indices that are out of bounds
			const validIndices = new Set<number>();
			prev.forEach((idx) => {
				if (idx >= 0 && idx < letterHeads.length) {
					validIndices.add(idx);
				}
			});
			return validIndices;
		});
	}, [letterHeads.length]);

	// Scroll to selected offices when a card is expanded or offices change
	useEffect(() => {
		// Use a small timeout to ensure DOM is ready
		const timeoutId = setTimeout(() => {
			letterHeads.forEach((letterHead, index) => {
				if (expandedIndices.has(index) && letterHead.officeNames && letterHead.officeNames.length > 0) {
					const container = officeContainerRefs.current[index];
					if (container) {
						// Find the first selected office checkbox
						const firstSelectedOffice = letterHead.officeNames[0];
						const officeIndex = availableOffices.indexOf(firstSelectedOffice);
						if (officeIndex !== -1) {
							// Get all label elements in the container
							const labels = container.querySelectorAll("label");
							if (labels[officeIndex]) {
								// Check if element is already visible in viewport
								const labelElement = labels[officeIndex] as HTMLElement;
								const containerRect = container.getBoundingClientRect();
								const labelRect = labelElement.getBoundingClientRect();
								
								// Only scroll if the element is not fully visible
								if (
									labelRect.top < containerRect.top ||
									labelRect.bottom > containerRect.bottom
								) {
									// Scroll the selected office into view
									labelElement.scrollIntoView({
										behavior: "smooth",
										block: "nearest",
										inline: "nearest",
									});
								}
							}
						}
					}
				}
			});
		}, 100);

		return () => clearTimeout(timeoutId);
	}, [expandedIndices, letterHeads, availableOffices]);

	const toggleExpand = (index: number) => {
		setExpandedIndices((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(index)) {
				newSet.delete(index);
			} else {
				newSet.add(index);
			}
			return newSet;
		});
	};

	const addLetterHead = () => {
		const newLetterHead: LetterHeadDetails = {
			path: "",
			scale: 1,
			margin: {
				top: 0,
				bottom: 0,
				left: 0,
				right: 0,
			},
			officeNames: [],
		};
		const newIndex = letterHeads.length;
		onLetterHeadsChange([...letterHeads, newLetterHead]);
		// Expand the newly added letterhead
		setExpandedIndices((prev) => new Set([...prev, newIndex]));
	};

	const removeLetterHead = (index: number) => {
		onLetterHeadsChange(letterHeads.filter((_, i) => i !== index));
		// Update expanded indices: remove the deleted index and adjust others
		setExpandedIndices((prev) => {
			const newSet = new Set<number>();
			prev.forEach((idx) => {
				if (idx < index) {
					// Keep indices before the removed one
					newSet.add(idx);
				} else if (idx > index) {
					// Shift indices after the removed one down by 1
					newSet.add(idx - 1);
				}
				// Skip the removed index
			});
			// If no indices are expanded and we still have letterheads, expand the first one
			if (newSet.size === 0 && letterHeads.length > 1) {
				newSet.add(0);
			}
			return newSet;
		});
	};

	const updateLetterHead = (index: number, updates: Partial<LetterHeadDetails>) => {
		const updated = [...letterHeads];
		updated[index] = { ...updated[index], ...updates };
		onLetterHeadsChange(updated);
	};

	const toggleOfficeSelection = (letterHeadIndex: number, officeId: string) => {
		const letterHead = letterHeads[letterHeadIndex];
		const officeNames = letterHead.officeNames || [];
		const newOfficeNames = officeNames.includes(officeId)
			? officeNames.filter((name) => name !== officeId)
			: [...officeNames, officeId];
		updateLetterHead(letterHeadIndex, { officeNames: newOfficeNames });
	};

	const getLetterHeadDisplayName = (letterHead: LetterHeadDetails, index: number, isExpanded: boolean): string => {
		// When collapsed, show office names if available
		if (!isExpanded && letterHead.officeNames && letterHead.officeNames.length > 0) {
			const officeNamesToShow = letterHead.officeNames.slice(0, 2);
			return `Letter Head ${officeNamesToShow.join(", ")}, ....`;
		}
		// Default to numbered name
		return `Letterhead #${index + 1}`;
	};

	return (
		<div className="space-y-4">
			<div className="flex justify-between items-center mb-4">
				<div className="text-gray-900 text-base font-medium font-['Hanken Grotesk']">Letter Head Details</div>
				<button
					type="button"
					onClick={addLetterHead}
					className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
				>
					+ Add Letterhead
				</button>
			</div>

			{letterHeads.length === 0 ? (
				<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center text-gray-500 text-sm">
					No letterheads configured. Click "Add Letterhead" to create one.
				</div>
			) : (
				letterHeads.map((letterHead, index) => {
					const isExpanded = expandedIndices.has(index);
					return (
						<div key={index} className="p-4 bg-white rounded-lg border border-gray-200 space-y-4">
							<div className="flex justify-between items-center">
								<div className="flex items-center space-x-2">
									<button
										type="button"
										onClick={() => toggleExpand(index)}
										className="p-1 hover:bg-gray-100 rounded transition-transform"
										aria-label={isExpanded ? "Collapse" : "Expand"}
									>
										<img
											src={isExpanded ? "/assets/ui/chevron-down.svg" : "/assets/ui/chevron-right.svg"}
											width="16"
											height="16"
											alt={isExpanded ? "Collapse" : "Expand"}
											className="transition-transform"
										/>
									</button>
									<div className="text-gray-700 text-sm font-medium">
										{getLetterHeadDisplayName(letterHead, index, isExpanded)}
									</div>
								</div>
								<button
									type="button"
									onClick={() => removeLetterHead(index)}
									className="px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
								>
									Remove
								</button>
							</div>

							{isExpanded && (
								<>
									{/* Office Selection */}
						<div>
							<label className="block text-gray-700 text-sm font-medium mb-2">Assign to Offices</label>
							<div className="text-gray-500 text-xs mb-2">
								Select offices for this letterhead, or leave unselected to apply to all offices
							</div>
							{availableOffices.length === 0 ? (
								<div className="text-gray-500 text-sm p-2 bg-gray-50 rounded">
									No offices available. This letterhead will apply to all offices. Configure offices in organization
									settings to assign specific offices.
								</div>
							) : (
								<div className="space-y-2">
									<div
										ref={(el) => {
											officeContainerRefs.current[index] = el;
										}}
										className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 rounded"
									>
										{availableOffices.map((office) => (
											<label
												key={office}
												className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
											>
												<input
													type="checkbox"
													checked={(letterHead.officeNames || []).includes(office)}
													onChange={() => toggleOfficeSelection(index, office)}
													className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
												/>
												<span className="text-sm text-gray-700">{office}</span>
											</label>
										))}
									</div>
									{(letterHead.officeNames || []).length === 0 && (
										<div className="text-blue-600 text-xs mt-1 font-medium">✓ Will apply to all offices</div>
									)}
								</div>
							)}
						</div>

						{/* Image Upload */}
						<ImageUploadField
							label="Letter Head Image"
							imagePath={letterHead.path}
							organizationId={organizationId}
							uploadDestination={`letterhead-${index}`}
							onImageChange={(path) => updateLetterHead(index, { path })}
							onImageRemove={() => updateLetterHead(index, { path: "" })}
							altText={`Letterhead ${index + 1} preview`}
						/>

						{/* Scale */}
						<div>
							<label className="block text-gray-700 text-sm font-medium mb-2">Scale</label>
							<input
								type="number"
								step="0.1"
								value={tempValues[`scale-${index}`] !== undefined ? tempValues[`scale-${index}`] : letterHead.scale ?? 1}
								onChange={(e) => {
									const value = e.target.value;
									// Store temporary value to allow empty input
									setTempValues((prev) => ({ ...prev, [`scale-${index}`]: value }));

									if (value === "" || value === "-" || value === "." || value.endsWith(".")) {
										// Allow empty or partial input while typing
										return;
									}

									const numValue = parseFloat(value);
									if (!isNaN(numValue)) {
										updateLetterHead(index, { scale: numValue });
										// Clear temp value once we have a valid number
										setTempValues((prev) => {
											const newVals = { ...prev };
											delete newVals[`scale-${index}`];
											return newVals;
										});
									}
								}}
								onBlur={(e) => {
									// Set default to 1 if empty on blur
									const value = e.target.value;
									if (value === "" || isNaN(parseFloat(value))) {
										updateLetterHead(index, { scale: 1 });
									}
									// Clear temp value on blur
									setTempValues((prev) => {
										const newVals = { ...prev };
										delete newVals[`scale-${index}`];
										return newVals;
									});
								}}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
							/>
						</div>

						{/* Margins */}
						<div>
							<label className="block text-gray-700 text-sm font-medium mb-2">Margins</label>
							<div className="grid grid-cols-2 gap-4">
								<div>
									<label className="block text-gray-600 text-xs mb-1">Top</label>
									<input
										type="number"
										value={
											tempValues[`margin-top-${index}`] !== undefined
												? tempValues[`margin-top-${index}`]
												: letterHead.margin?.top ?? 0
										}
										onChange={(e) => {
											const value = e.target.value;
											setTempValues((prev) => ({ ...prev, [`margin-top-${index}`]: value }));

											if (value === "" || value === "-" || value === "." || value.endsWith(".")) {
												return;
											}

											const numValue = parseFloat(value);
											if (!isNaN(numValue)) {
												updateLetterHead(index, {
													margin: { ...letterHead.margin, top: numValue },
												});
												setTempValues((prev) => {
													const newVals = { ...prev };
													delete newVals[`margin-top-${index}`];
													return newVals;
												});
											}
										}}
										onBlur={(e) => {
											const value = e.target.value;
											if (value === "" || isNaN(parseFloat(value))) {
												updateLetterHead(index, {
													margin: { ...letterHead.margin, top: 0 },
												});
											}
											setTempValues((prev) => {
												const newVals = { ...prev };
												delete newVals[`margin-top-${index}`];
												return newVals;
											});
										}}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
									/>
								</div>
								<div>
									<label className="block text-gray-600 text-xs mb-1">Bottom</label>
									<input
										type="number"
										value={
											tempValues[`margin-bottom-${index}`] !== undefined
												? tempValues[`margin-bottom-${index}`]
												: letterHead.margin?.bottom ?? 0
										}
										onChange={(e) => {
											const value = e.target.value;
											setTempValues((prev) => ({ ...prev, [`margin-bottom-${index}`]: value }));

											if (value === "" || value === "-" || value === "." || value.endsWith(".")) {
												return;
											}

											const numValue = parseFloat(value);
											if (!isNaN(numValue)) {
												updateLetterHead(index, {
													margin: { ...letterHead.margin, bottom: numValue },
												});
												setTempValues((prev) => {
													const newVals = { ...prev };
													delete newVals[`margin-bottom-${index}`];
													return newVals;
												});
											}
										}}
										onBlur={(e) => {
											const value = e.target.value;
											if (value === "" || isNaN(parseFloat(value))) {
												updateLetterHead(index, {
													margin: { ...letterHead.margin, bottom: 0 },
												});
											}
											setTempValues((prev) => {
												const newVals = { ...prev };
												delete newVals[`margin-bottom-${index}`];
												return newVals;
											});
										}}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
									/>
								</div>
								<div>
									<label className="block text-gray-600 text-xs mb-1">Left</label>
									<input
										type="number"
										value={
											tempValues[`margin-left-${index}`] !== undefined
												? tempValues[`margin-left-${index}`]
												: letterHead.margin?.left ?? 0
										}
										onChange={(e) => {
											const value = e.target.value;
											setTempValues((prev) => ({ ...prev, [`margin-left-${index}`]: value }));

											if (value === "" || value === "-" || value === "." || value.endsWith(".")) {
												return;
											}

											const numValue = parseFloat(value);
											if (!isNaN(numValue)) {
												updateLetterHead(index, {
													margin: { ...letterHead.margin, left: numValue },
												});
												setTempValues((prev) => {
													const newVals = { ...prev };
													delete newVals[`margin-left-${index}`];
													return newVals;
												});
											}
										}}
										onBlur={(e) => {
											const value = e.target.value;
											if (value === "" || isNaN(parseFloat(value))) {
												updateLetterHead(index, {
													margin: { ...letterHead.margin, left: 0 },
												});
											}
											setTempValues((prev) => {
												const newVals = { ...prev };
												delete newVals[`margin-left-${index}`];
												return newVals;
											});
										}}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
									/>
								</div>
								<div>
									<label className="block text-gray-600 text-xs mb-1">Right</label>
									<input
										type="number"
										value={
											tempValues[`margin-right-${index}`] !== undefined
												? tempValues[`margin-right-${index}`]
												: letterHead.margin?.right ?? 0
										}
										onChange={(e) => {
											const value = e.target.value;
											setTempValues((prev) => ({ ...prev, [`margin-right-${index}`]: value }));

											if (value === "" || value === "-" || value === "." || value.endsWith(".")) {
												return;
											}

											const numValue = parseFloat(value);
											if (!isNaN(numValue)) {
												updateLetterHead(index, {
													margin: { ...letterHead.margin, right: numValue },
												});
												setTempValues((prev) => {
													const newVals = { ...prev };
													delete newVals[`margin-right-${index}`];
													return newVals;
												});
											}
										}}
										onBlur={(e) => {
											const value = e.target.value;
											if (value === "" || isNaN(parseFloat(value))) {
												updateLetterHead(index, {
													margin: { ...letterHead.margin, right: 0 },
												});
											}
											setTempValues((prev) => {
												const newVals = { ...prev };
												delete newVals[`margin-right-${index}`];
												return newVals;
											});
										}}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
									/>
								</div>
							</div>
						</div>

						{/* Preview Scale */}
						<div>
							<label className="block text-gray-700 text-sm font-medium mb-1">Preview Scale (optional)</label>
							<input
								type="number"
								step="0.1"
								value={letterHead.previewScale || ""}
								onChange={(e) => {
									const numValue = parseFloat(e.target.value);
									updateLetterHead(index, {
										previewScale:
											e.target.value === "" ? undefined : isNaN(numValue) ? letterHead.previewScale : numValue,
									});
								}}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
								placeholder="Optional"
							/>
						</div>

						{/* Preview Alignment */}
						<div>
							<label className="block text-gray-700 text-sm font-medium mb-1">Preview Alignment (optional)</label>
							<select
								value={letterHead.previewAlignment || ""}
								onChange={(e) =>
									updateLetterHead(index, {
										previewAlignment: e.target.value as "left" | "center" | "right" | undefined,
									})
								}
								className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
							>
								<option value="">None</option>
								<option value="left">Left</option>
								<option value="center">Center</option>
								<option value="right">Right</option>
							</select>
						</div>
					</>
				)}
				</div>
			);
			})
			)}
		</div>
	);
};

export default LetterHeadManagement;
